import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LikeButton = ({ contentId, initialLiked = false, initialCount = 0, size = 'default', showCount = true, className = '' }) => {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [isLiked, setIsLiked] = useState(initialLiked);
  const [likeCount, setLikeCount] = useState(initialCount);
  const [isLoading, setIsLoading] = useState(false);

  // Check if user has liked this content
  useEffect(() => {
    const checkLikeStatus = async () => {
      if (!token || !contentId) return;
      
      try {
        const response = await axios.get(`${API_URL}/api/social/likes/${contentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check if current user is in the likes list
        const userLiked = response.data.users?.some(u => u.id === user?.id);
        setIsLiked(userLiked);
        setLikeCount(response.data.total || 0);
      } catch (error) {
        console.error('Error checking like status:', error);
      }
    };

    checkLikeStatus();
  }, [contentId, token, user?.id]);

  const handleLike = async (e) => {
    e.stopPropagation();
    
    if (!token) {
      toast({
        variant: 'destructive',
        title: 'Login Required',
        description: 'Please log in to like content',
      });
      return;
    }

    setIsLoading(true);
    
    try {
      if (isLiked) {
        // Unlike
        await axios.delete(`${API_URL}/api/social/unlike/${contentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsLiked(false);
        setLikeCount(prev => Math.max(0, prev - 1));
        toast({
          title: 'Removed',
          description: 'Removed from your likes',
        });
      } else {
        // Like
        await axios.post(`${API_URL}/api/social/like/${contentId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsLiked(true);
        setLikeCount(prev => prev + 1);
        toast({
          title: 'Liked!',
          description: 'Added to your likes',
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update like',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'w-8 h-8 p-0',
    default: 'w-10 h-10 p-0',
    lg: 'w-12 h-12 p-0'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    default: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Button
        variant="ghost"
        size={size}
        onClick={handleLike}
        disabled={isLoading}
        className={`text-white hover:text-white border border-gray-600 hover:border-white rounded-full ${sizeClasses[size]} transition-all duration-200 ${isLiked ? 'bg-red-500/20 border-red-500 hover:border-red-400' : ''}`}
        title={isLiked ? 'Unlike' : 'Like'}
      >
        <Heart 
          className={`${iconSizes[size]} transition-all duration-200 ${isLiked ? 'fill-red-500 text-red-500' : ''} ${isLoading ? 'animate-pulse' : ''}`} 
        />
      </Button>
      {showCount && likeCount > 0 && (
        <span className="text-xs text-gray-400">{likeCount}</span>
      )}
    </div>
  );
};

export default LikeButton;
